import React from 'react';
import styled from 'styled-components';
import { handLineAfter } from '../../handLine';
import CaseStudyTemplate, {
  CaseStudyFigure,
  CaseStudySection,
  StoryParagraph,
  freebitesAccentPillCss,
} from './CaseStudyTemplate';

const FREEBITES_INSTAGRAM_REELS_HREF =
  'https://www.instagram.com/freebites.app/reels/';

/** Edit `href` per cover to point at a specific reel or post. */
const reelCovers = [
  {
    src: '/freebites-reel-cover.png',
    href: 'https://www.instagram.com/p/DDkpfGwRt--/',
    ariaLabel: 'Open Freebites Instagram — marketing reel 1',
  },
  {
    src: '/freebites-reel-cover2.png',
    href: 'https://www.instagram.com/p/DGmhlg3gdMH/',
    ariaLabel: 'Open Freebites Instagram — marketing reel 2',
  },
  {
    src: '/freebites-reel-cover3.png',
    href: 'https://www.instagram.com/p/DPmPcwHgCBL/',
    ariaLabel: 'Open Freebites Instagram — marketing reel 3',
  },
];

const APP_STORE_REVIEWS_HREF =
  'https://apps.apple.com/us/app/freebites/id6664051907?see-all=reviews&platform=iphone';

const JumpToResultsLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 0.35rem;
  padding: 0.48rem 0.95rem;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 999px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  ${freebitesAccentPillCss}
`;

const TextLink = styled.a`
  position: relative;
  color: inherit;
  text-decoration: none;

  &::after {
    ${handLineAfter}
  }

  &:hover {
    color: var(--text);
  }

  &:hover::after {
    opacity: 1;
    transform: scaleY(1.2);
  }
`;

const BlockLabel = styled.p`
  margin: 2rem 0 0.65rem;
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
`;

const StorySubheading = styled.h3`
  margin: 1.35rem 0 0.65rem;
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  color: var(--text);
  letter-spacing: -0.02em;
  line-height: 1.35;

  &:first-child {
    margin-top: 0;
  }
`;

const StoryBulletList = styled.ul`
  margin: 0 0 1rem;
  padding: 0 0 0 1.25rem;
  font-size: 18px;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;
`;

const StoryListItem = styled.li`
  margin: 0 0 0.55rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PullQuote = styled.blockquote`
  margin: 1.25rem 0 1rem;
  padding: 1rem 1.1rem 1rem 1.15rem;
  border-left: 3px solid var(--border);
  background: var(--surface);
  font-size: 18px;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;

  strong {
    color: var(--text);
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ReelRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 1.35rem;
  width: 100%;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ReelCoverLink = styled.a.attrs({
  target: '_blank',
  rel: 'noopener noreferrer',
})`
  display: block;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  img {
    display: block;
    width: 100%;
    height: auto;
    vertical-align: middle;
    transform: scale(1);
    transition: transform 0.28s ease;
  }

  &:hover,
  &:focus-visible {
    border-color: var(--text);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.08);
  }

  &:hover img,
  &:focus-visible img {
    transform: scale(1.06);
  }
`;

const ReviewCard = styled.a.attrs({ target: '_blank', rel: 'noopener noreferrer' })`
  display: block;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  padding: 1rem 0.95rem 1rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--text);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.05);
  }
`;

const Stars = styled.span`
  display: block;
  margin: 0 0 0.45rem;
  font-size: 16px;
  letter-spacing: 0.04em;
  color: #e6ac00;
`;

const ReviewHeadline = styled.p`
  margin: 0 0 0.35rem;
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
  line-height: 1.3;
`;

const ReviewMeta = styled.p`
  margin: 0 0 0.45rem;
  font-size: 16px;
  color: var(--text-muted);
`;

const ReviewQuote = styled.p`
  margin: 0;
  font-size: 18px;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;
`;

const PressCard = styled.a.attrs({ target: '_blank', rel: 'noopener noreferrer' })`
  display: block;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  padding: 1rem 0.95rem 1rem;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--text);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.05);
  }
`;

const PressSource = styled.p`
  margin: 0 0 0.35rem;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-muted);
`;

const PressHeadline = styled.p`
  margin: 0 0 0.45rem;
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
  line-height: 1.35;
`;

const PressMeta = styled.p`
  margin: 0;
  font-size: 16px;
  color: var(--text-muted);
`;

const marketingReviews = [
  {
    headline: 'just what tufts needed',
    meta: '10/18/2024 · Kchian',
    quote:
      '... Turning this idea into an app really makes things better for everyone. Now I can get a direct notification about food opportuniites straight from one app!',
  },
  {
    headline: 'Fantastic app!',
    meta: '4/10/2025 · parthenon97',
    quote: 
    'Love the mission of this app, and it\'s super intuitive to use! Wish it was available at all colleges.'
  },
  {
    headline: 'It works so well!',
    meta: '10/15/2024 · johnnytan18',
    quote:
    'People are posting free food around campus and I can get notified. It\'s super helpful!'
  },
  {
    headline: 'FreeBites made me love food',
    meta: '12/14/2024 · wkim10',
    quote: 'I\'m always looking out for those free food notifications! I love FreebBites 🫶🫶',
  },
  {
    headline: 'Really useful app',
    meta: '4/3/2025 · FreebitesBest',
    quote:
    'Developed by students, for students! Such a great initiative to help solve food insecurity. '
  },
  {
    headline: 'love it!',
    meta: '12/9/2024 · js82071',
    quote: 'I wish this was available outside of tufts!',
  },
];

const pressItems = [
  {
    url: 'https://www.tuftsdaily.com/article/2025/03/freebites-app-makes-free-food-a-click-away',
    source: 'The Tufts Daily',
    headline: 'Freebites app makes free food a click away',
    meta: 'Dylan Fee · March 6, 2025',
  },
  {
    url: 'https://now.tufts.edu/2025/01/28/need-sublet-how-about-coffee-or-free-pizza-students-make-apps',
    source: 'Tufts Now',
    headline: 'Need a Sublet? How About Coffee or Free Pizza? Students Make Apps for That',
    meta: 'Laura Ferguson · January 28, 2025',
  },
  {
    url: 'https://gordon.tufts.edu/idea-impact/freebites-eliminating-college-food-waste-and-insecurity',
    source: 'Tufts Gordon Institute',
    headline: 'Freebites: Eliminating college food waste and insecurity',
    meta: 'From Idea to Impact Feature',
  },
  {
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7299176602406449152/',
    source: 'Hult Prize at Tufts',
    headline: 'First runner-up, Tufts Hult Prize Competition',
    meta: 'Pitch Competition Winner Announcement',
  },
];

const impactLead = (
  <>
    <StoryParagraph>
      Currnet update: Freebites grew to <strong>6k+ downloads</strong>, expanded across <strong>four Greater Boston schools</strong>, won <strong>five awards</strong>, and $10k+ in funding. What started as a small team is now a <strong>20-person</strong> team building this <strong>501(c)(3)</strong> nonprofit app.
    </StoryParagraph>
    <JumpToResultsLink href="#outcomes">Jump to Results</JumpToResultsLink>
  </>
);

const FreebitesCaseStudy = () => {
  return (
    <CaseStudyTemplate
      title="Building Freebites, a college campus food app for thousands of students"
      intro="As the cofounder and founding product designer, I led the zero-to-one product strategy and end-to-end experience design for a 501(c)(3) nonprofit app, connecting students to surplus food in real-time."
      details={[
        { label: 'Role', value: 'Cofounder, Founding Product Designer' },
        { label: 'Timeline', value: '2023 to present' },
        { label: 'Scale', value: '4k+ downloads, multi-campus rollout, 20-person team' },
        { label: 'Platform', value: 'iOS mobile app' },
        {
          value: 'View live product',
          href: 'https://www.freebites.org/',
          accent: true,
        },
      ]}
      afterIntro={impactLead}
      sectionNav={[
        { id: 'problem', label: 'Problem' },
        { id: 'research', label: 'Research' },
        { id: 'design', label: 'Design' },
        { id: 'testing', label: 'Testing' },
        { id: 'tradeoffs', label: 'Tradeoffs' },
        { id: 'outcomes', label: 'Results' },
      ]}
      heroImageSrc="/freebites.png"
      heroImageAlt="Freebites before and after redesign: home feed, multi-step create post flow, and post detail with comments and share"
      backTarget="selected-work"
    >
      <CaseStudySection
        id="problem"
        title="The problem: free food that disappears before anyone sees it"
      >
        <StoryParagraph>
          Students navigate two pressures at once: limited food budgets and piles of untouched event leftovers. The systems meant to connect them (group chats, flyers, word of mouth) were too slow when free food disappears in minutes or goes to waste.
        </StoryParagraph>
        <StoryParagraph>
          The opportunity wasn&apos;t another notification channel. It was to make discovery feel as urgent and local as the food itself, without adding noise to already-crowded inboxes.
        </StoryParagraph>
        <StoryParagraph>I framed the experience around three beats:</StoryParagraph>
        <StoryBulletList>
          <StoryListItem>
            <strong>Scan quickly</strong> — the feed had to communicate what, where, and when at a glance
          </StoryListItem>
          <StoryListItem>
            <strong>Trust what you see</strong> — posts needed enough signal that users felt confident actually going
          </StoryListItem>
          <StoryListItem>
            <strong>Post in under a minute</strong> — if sharing was hard, the supply side would collapse
          </StoryListItem>
        </StoryBulletList>
        <StoryParagraph>
          Everything else like filters, comments, threading had to earn its place by reinforcing those three beats. If it didn&apos;t serve one of them, it didn&apos;t ship.
        </StoryParagraph>
      </CaseStudySection>

      <CaseStudySection id="research" title="What I learned from talking to students">
        <StoryParagraph>
          Before any design work, I ran a pre-launch survey of the Tufts community to measure baseline demand and recruit research participants. I also wrote a moderated usability brief with explicit KPIs — task success rate, error rate, time-on-task, effectiveness, efficiency, and satisfaction — so we could score sessions against something concrete instead of gut feel.
        </StoryParagraph>
        <StoryParagraph>
          Sessions ran remotely over short windows with diverse participants. I captured outcomes in a lightweight task-analysis grid (success, close calls, friction, outright errors) so we could prioritize fixes ahead of wider rollout.
        </StoryParagraph>
        <CaseStudyFigure
          src="/freebites-researchsynthesis.png"
          alt="Collage of Freebites pre-launch Google Form survey, usability interview brief with research questions and KPIs, and task-analysis spreadsheet scoring participant tasks"
          caption="Survey for baseline demand, a structured interview guide with explicit success metrics, and task-by-task scoring to turn qualitative sessions into an actionable backlog."
        />
        <StorySubheading>The finding that changed everything</StorySubheading>
        <StoryParagraph>
          We expected the core friction to be on the discovery side — students not finding food fast enough. Testing revealed a different problem: <strong>users didn&apos;t trust the posts.</strong>
        </StoryParagraph>
        <StoryParagraph>
          When someone found a listing on existing campus food resources, they weren&apos;t confident enough to act on it. Was the food still there? Was it safe to eat? Did they need to bring utensils? Without answers, they scrolled past.
        </StoryParagraph>
        <StoryParagraph>
          That insight reframed the entire design direction where trust wasn&apos;t a nice-to-have but the thing to design for. 
        </StoryParagraph>
        <StorySubheading>Location mattered more than food type</StorySubheading>
        <StoryParagraph>
          Another major discovery was that our original search hypothesis was wrong. We assumed students filtered by what food was available. Testing showed they cared more about where it was — proximity drove pickup decisions more than food preferences.
        </StoryParagraph>
        <StoryParagraph>This also sharpened how I thought about the two sides of the product:</StoryParagraph>
        <StoryBulletList>
          <StoryListItem>
            <strong>Posters</strong> needed a fast, low-friction way to create a post worth trusting
          </StoryListItem>
          <StoryListItem>
            <strong>Grabbers</strong> needed to scan, evaluate, and navigate before the food was gone
          </StoryListItem>
        </StoryBulletList>
        <StoryParagraph>
          The design had to work for both without making either feel secondary.
        </StoryParagraph>
      </CaseStudySection>

      <CaseStudySection id="design" title="From sketches to a system">
        <StorySubheading>Ideation and early flows</StorySubheading>
        <StoryParagraph>
          Early flows were sketched to stress-test the posting journey: capture media, describe the food, layer multiple food items, attach filters, confirm location, post. These low-fidelity sketches let us move fast and catch structural problems before any engineering constraints crept in.
        </StoryParagraph>
        <StoryParagraph>
          These wireframes tightened the sequence into clear steps with a persistent primary action. The goal was to reduce decisions — every required field had a default or a visual example so users knew what a good post looked like before they started.
        </StoryParagraph>
        <CaseStudyFigure
          src="/freebites-initialsketch.png"
          alt="Hand-drawn Freebites screens for camera, post description, multi-item overview, filters, and finalize post beside digital low-fidelity wireframes for the same flow"
          caption="Paper storyboards explored multi-item posts, per-item filters, and location capture; digital wires tightened the sequence into clear steps with a persistent primary action."
        />
        <StorySubheading>Responding to the trust problem</StorySubheading>
        <StoryParagraph>
          After research, I added required fields to every post: a photo, description, food type, location, and storage type. These weren&apos;t bureaucratic checkboxes — they were the exact signals users said they needed to feel confident going to pick something up, and they aligned with how people already think about food safety.
        </StoryParagraph>
        <StoryParagraph>
          The creation wizard was redesigned as a guided flow with explicit progress. Posting friction had been one of our biggest failure points in testing; the wizard cut abandonment by making each step small and the end clearly in sight.
        </StoryParagraph>
        <StorySubheading>High-fidelity and the system behind it</StorySubheading>
        <StoryParagraph>
          High-fidelity work expanded into the full product surface: discovery feed, multi-step creation wizard, post detail view, profile, settings, notifications, and edge cases like expired posts and empty states.
        </StoryParagraph>
        <PullQuote>
          <strong>The design constraint that shaped everything:</strong> the app had to be legible in noisy dining halls and dim lecture hallways. High contrast, large touch targets, and a visual hierarchy that communicated food type, location, and time remaining before a user had fully stopped walking.
        </PullQuote>
        <StoryParagraph>
          Multiple iterations, experiements, and testing was done to refine the design. Below are some of the designs that didn't make the cut.
        </StoryParagraph>
        <CaseStudyFigure
          src="/freebites-initialsketch2.png"
          alt="Grid of high-fidelity Freebites mobile screens showing feed, search, post creation, food details, profile, settings, notifications, and confirmation states"
          caption="From postings with no image of the food to heat map testing, these feed, creation wizard, post detail, profile, settings, and notification touchpoints were iterated on extensively."
        />
      </CaseStudySection>

      <CaseStudySection id="testing" title="Usability testing and iteration">
        <StoryParagraph>
          Remote sessions had participants complete realistic tasks while thinking aloud. I paired screen recordings with note-taking to catch breakdowns in discoverability and to track emotional response to the brand across campuses.
        </StoryParagraph>
        <CaseStudyFigure
          src="/freebites-usertesting.png"
          alt="Collage of remote user testing video calls with participants using the Freebites mobile app"
          caption="Short, remote sessions kept tests consistent across campuses while still surfacing how students scan under time pressure, filter by their preferences, and decide in seconds whether a post is worth the trip."
        />
        <StoryParagraph>
          The before-and-after iterations addressed three things directly based on testing:
        </StoryParagraph>
        <StoryBulletList>
          <StoryListItem>
            <strong>Trust:</strong> Required fields and photos made posts credible enough to act on
          </StoryListItem>
          <StoryListItem>
            <strong>Posting friction:</strong> The guided wizard with clear progress reduced abandonment
          </StoryListItem>
          <StoryListItem>
            <strong>Feed clarity:</strong> Reduced surface noise so the three most important pieces of information — food type, location, time posted — read in under two seconds
          </StoryListItem>
        </StoryBulletList>
        <CaseStudyFigure
          src="/freebites-usertestingfeedback.png"
          alt="Four panels of Freebites user testing video calls with speech bubbles quoting participants on filters, sharing posts, multi-post needs, and posting difficulty"
          caption="Verbatim quotes grounded the roadmap: make filters obvious, reduce posting friction, explore sharing and multi-post needs, and celebrate strong intent to use the product."
        />
      </CaseStudySection>

      <CaseStudySection id="tradeoffs" title={"Tradeoffs and what didn't make the cut"}>
        <StoryParagraph>
          <strong>Granular filters got moved.</strong> An early version surfaced detailed dietary filters at the browsing stage. Testing showed users didn&apos;t engage with them there — they made filter decisions at the post detail level, once they&apos;d already found something worth looking at. We moved filters downstream and simplified the feed.
        </StoryParagraph>
        <StoryParagraph>
          <strong>Draft saving was scoped down.</strong> Early in the posting flow, we designed the ability to save incomplete posts — so if a user abandoned mid-way, they could return and finish later. Engineering scoped it out due to complexity and competing priorities. The real design response was to make abandonment less likely in the first place: the creation wizard was tightened into smaller, faster steps with a persistent progress indicator so completing a post felt achievable in a single session. If posting took under a minute, drafts mattered less.
        </StoryParagraph>
        <StoryParagraph>
          <strong>Category-first feed was deprecated.</strong> An early version organized the feed by food category. Testing showed users ignored the categories and scrolled chronologically. We cut the category view and moved to a time-and-distance feed. Simpler, and closer to how people actually scanned.
        </StoryParagraph>
      </CaseStudySection>

      <CaseStudySection id="outcomes" title="Results and Impact">
      <StoryParagraph>
          <strong>Freebites launched around September 2023</strong> with over 1,000 downloads in the first week on a single campus. Since then:
 
        </StoryParagraph>
        <StoryBulletList>
          <StoryListItem>
            <strong>4k+ downloads</strong> across Tufts, Harvard, Babson, and Northeastern.
          </StoryListItem>
          <StoryListItem>
            <strong>Five awards</strong> from pitch competitions recognizing mission and execution.
          </StoryListItem>
          <StoryListItem>
            <strong>20-person</strong> team of amazing developers, designers, and marketers consistently working together improve this <strong>501(c)(3)</strong> nonprofit app.
          </StoryListItem>
          <StoryListItem>
            <strong>40k+ views</strong> on social media platforms the team produced to introduce Freebites in a format native to how they already consume content and news. 
          </StoryListItem>
        </StoryBulletList>
        <BlockLabel>Social Media</BlockLabel>
        <StoryParagraph>
          The team used {' '}
          <TextLink
            href={FREEBITES_INSTAGRAM_REELS_HREF}
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram Reels
          </TextLink>{' '}
          to show Freebites in action with real students, real pickups, and real impact. 
        </StoryParagraph>
        <ReelRow>
          {reelCovers.map((reel) => (
            <ReelCoverLink key={reel.src} href={reel.href} aria-label={reel.ariaLabel}>
              <img src={reel.src} alt="" loading="lazy" />
            </ReelCoverLink>
          ))}
        </ReelRow>

        <BlockLabel>Reviews</BlockLabel>
        <StoryParagraph>
        Some users left reviews on the App Store to show their support for the mission and the app.
        </StoryParagraph>
        <CardGrid>
          {marketingReviews.map((rev) => (
            <ReviewCard
              key={`${rev.meta}-${rev.headline}`}
              href={APP_STORE_REVIEWS_HREF}
              title="View ratings and reviews on the App Store"
            >
              <Stars aria-label="Rated 5 out of 5">★★★★★</Stars>
              <ReviewHeadline>{rev.headline}</ReviewHeadline>
              <ReviewMeta>{rev.meta}</ReviewMeta>
              <ReviewQuote>{rev.quote}</ReviewQuote>
            </ReviewCard>
          ))}
        </CardGrid>

        <BlockLabel>Coverage</BlockLabel>
        <StoryParagraph>
        I competed in many pitch competitions and won awards for the app that gained attention from the media and the community.
        </StoryParagraph>
        <CardGrid>
          {pressItems.map((item) => (
            <PressCard key={item.url} href={item.url}>
              <PressSource>{item.source}</PressSource>
              <PressHeadline>{item.headline}</PressHeadline>
              <PressMeta>{item.meta}</PressMeta>
            </PressCard>
          ))}
        </CardGrid>
      </CaseStudySection>
    </CaseStudyTemplate>
  );
};

export default FreebitesCaseStudy;
